export interface TelemetryPoint {
  lat: number;
  lon: number;
  ele: number;
  time: string;
  hr?: number;
  cadence?: number;
  power?: number;
  speed?: number;
  distance?: number;
}

export interface CameraKeyframe {
  center: [number, number];
  zoom: number;
  pitch: number;
  bearing: number;
}

export interface MapConstraints {
  minZoom: number;
  maxBounds: [[number, number], [number, number]]; // [[sw_lng, sw_lat], [ne_lng, ne_lat]]
}

export interface CameraProfile {
  baseAltitude: number;
  pitch: number;
  lookAheadPoints: number;
  terrainExaggeration: number;
  introZoom: number;
  speedCurve: 'linear' | 'ease-in-out' | 'slow-dramatic';
}

export const PROFILES: Record<string, CameraProfile> = {
  running: {
    baseAltitude: 150,
    pitch: 55,
    lookAheadPoints: 8,
    terrainExaggeration: 1.5,
    introZoom: 12,
    speedCurve: 'linear',
  },
  cycling: {
    baseAltitude: 300,
    pitch: 45,
    lookAheadPoints: 15,
    terrainExaggeration: 1.4,
    introZoom: 10,
    speedCurve: 'linear',
  },
  hiking: {
    baseAltitude: 200,
    pitch: 50,
    lookAheadPoints: 6,
    terrainExaggeration: 1.7,
    introZoom: 11,
    speedCurve: 'ease-in-out',
  },
  climbing: {
    baseAltitude: 180,
    pitch: 50,
    lookAheadPoints: 4,
    terrainExaggeration: 1.7,
    introZoom: 13,
    speedCurve: 'slow-dramatic',
  },
  other: {
    baseAltitude: 200,
    pitch: 50,
    lookAheadPoints: 8,
    terrainExaggeration: 1.5,
    introZoom: 11,
    speedCurve: 'linear',
  },
};

export function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function calculateBearing(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLon = toRadians(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(toRadians(lat2));
  const x =
    Math.cos(toRadians(lat1)) * Math.sin(toRadians(lat2)) -
    Math.sin(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.cos(dLon);
  const bearing = Math.atan2(y, x);
  return ((bearing * 180) / Math.PI + 360) % 360;
}

export class CameraAnimator {
  private keyframes: CameraKeyframe[] = [];
  private totalDistance: number = 0;
  private points: TelemetryPoint[] = [];
  private profile: CameraProfile;
  private introFrame!: CameraKeyframe;
  private outroFrame!: CameraKeyframe;
  private width: number;
  private height: number;
  public introRatio: number;
  public outroRatio: number;
  private trackMinLat = Infinity;
  private trackMaxLat = -Infinity;
  private trackMinLon = Infinity;
  private trackMaxLon = -Infinity;
  private fittedZoom = 1;

  constructor(
    points: TelemetryPoint[],
    profile: CameraProfile,
    width?: number,
    height?: number,
    introRatio?: number,
    outroRatio?: number,
  ) {
    this.points = points;
    this.profile = profile;
    this.width = width || (typeof window !== 'undefined' ? window.innerWidth : 1920);
    this.height = height || (typeof window !== 'undefined' ? window.innerHeight : 1080);
    this.introRatio = introRatio !== undefined ? introRatio : 0.08;
    this.outroRatio = outroRatio !== undefined ? outroRatio : 0.1;

    if (points.length < 2) {
      throw new Error('Track must have at least 2 points to animate');
    }

    this.calculateDistances();
    this.totalDistance = this.points[this.points.length - 1].distance || 0;
    this.generateKeyframes();
    this.setupIntroOutro();
    console.log(
      `[AnimatorConstructor] Points count=${this.points.length} | trackBounds=[${this.trackMinLon.toFixed(5)}, ${this.trackMinLat.toFixed(5)} => ${this.trackMaxLon.toFixed(5)}, ${this.trackMaxLat.toFixed(5)}] | introFrame.center=${JSON.stringify(this.introFrame.center)} | keyframes[0].center=${JSON.stringify(this.keyframes[0]?.center)}`,
    );
  }

  private calculateDistances(): void {
    let dist = 0;
    this.points[0].distance = 0;
    for (let i = 1; i < this.points.length; i++) {
      const prev = this.points[i - 1];
      const curr = this.points[i];
      dist += haversineDistance(prev.lat, prev.lon, curr.lat, curr.lon);
      curr.distance = dist;
    }
  }

  private getInterpolatedPoint(distance: number): TelemetryPoint {
    if (distance <= 0) return this.points[0];
    if (distance >= this.totalDistance) return this.points[this.points.length - 1];

    let low = 0;
    let high = this.points.length - 1;
    while (low < high - 1) {
      const mid = Math.floor((low + high) / 2);
      if ((this.points[mid].distance || 0) <= distance) {
        low = mid;
      } else {
        high = mid;
      }
    }

    const p1 = this.points[low];
    const p2 = this.points[high];
    const d1 = p1.distance || 0;
    const d2 = p2.distance || 0;
    const t = (distance - d1) / (d2 - d1 || 1);

    return {
      lat: p1.lat + t * (p2.lat - p1.lat),
      lon: p1.lon + t * (p2.lon - p1.lon),
      ele: p1.ele + t * (p2.ele - p1.ele),
      time: p1.time,
      speed: p1.speed !== undefined && p2.speed !== undefined ? p1.speed + t * (p2.speed - p1.speed) : undefined,
      hr: p1.hr,
      cadence: p1.cadence,
      power: p1.power,
      distance: distance,
    };
  }

  private generateKeyframes(): void {
    const NUM_KEYFRAMES = 100;
    const lookAheadDist = Math.max(100, this.profile.baseAltitude * 0.7);
    const pitchRad = (this.profile.pitch * Math.PI) / 180;
    const baseZoom = Math.log2(37500000 / this.profile.baseAltitude);

    // The camera sits behind and above the center point.
    // At pitch P, the horizontal offset behind center = baseAltitude * sin(P)
    // and altitude above center = baseAltitude * cos(P).
    // We scan a window of track points around each keyframe to find the max
    // terrain elevation that the camera might clip into.
    const horizontalOffset = this.profile.baseAltitude * Math.sin(pitchRad);
    // Generous look-behind window: 2× the horizontal offset
    const lookBehindDist = horizontalOffset * 2;
    // Safety margin in real meters above terrain before exaggeration
    const safetyMarginMeters = 80;

    for (let k = 0; k < NUM_KEYFRAMES; k++) {
      const progress = k / (NUM_KEYFRAMES - 1);
      const dist = progress * this.totalDistance;

      const pt = this.getInterpolatedPoint(dist);
      const lookAheadTarget = Math.min(dist + lookAheadDist, this.totalDistance);
      const lookAheadPt = this.getInterpolatedPoint(lookAheadTarget);

      let bearing = 0;
      if (lookAheadTarget > dist) {
        bearing = calculateBearing(pt.lat, pt.lon, lookAheadPt.lat, lookAheadPt.lon);
      } else {
        const prevDist = Math.max(0, dist - 50);
        const prevPt = this.getInterpolatedPoint(prevDist);
        bearing = calculateBearing(prevPt.lat, prevPt.lon, pt.lat, pt.lon);
      }

      // Find max elevation in the window around this keyframe.
      // This approximates the terrain the camera might fly over/through.
      const windowStart = Math.max(0, dist - lookBehindDist);
      const windowEnd = Math.min(this.totalDistance, dist + lookAheadDist * 0.5);
      let maxEle = pt.ele;

      const sampleCount = 20;
      for (let s = 0; s <= sampleCount; s++) {
        const sampleDist = windowStart + (windowEnd - windowStart) * (s / sampleCount);
        const samplePt = this.getInterpolatedPoint(sampleDist);
        if (samplePt.ele > maxEle) {
          maxEle = samplePt.ele;
        }
      }

      // Calculate whether the camera would clip into terrain.
      // With terrain exaggeration, elevations are visually multiplied.
      // The camera altitude above center = baseAltitude * cos(pitch).
      // We need: centerEle*exag + altitude > maxEle*exag + safetyMargin
      // => altitude > (maxEle - centerEle)*exag + safetyMargin
      const eleDiffVisual = (maxEle - pt.ele) * this.profile.terrainExaggeration;
      const requiredAltAboveCenter = eleDiffVisual + safetyMarginMeters;
      const requiredDistance = requiredAltAboveCenter / Math.cos(pitchRad);

      // Only zoom OUT (increase distance), never zoom in closer than base
      const effectiveDistance = Math.max(this.profile.baseAltitude, requiredDistance);
      const zoom = Math.log2(37500000 / effectiveDistance);

      this.keyframes.push({
        center: [pt.lon, pt.lat],
        zoom: Math.min(baseZoom, zoom),
        pitch: this.profile.pitch,
        bearing,
      });
    }

    this.smoothBearings();
    this.smoothZooms();
  }

  /**
   * Smooth zoom values to avoid abrupt changes and anticipate terrain rises.
   * 1. Forward-propagate the min zoom so the camera starts pulling back
   *    BEFORE hitting a mountain.
   * 2. Apply a moving average for gradual transitions.
   */
  private smoothZooms(): void {
    const rawZooms = this.keyframes.map((k) => k.zoom);

    // Pass 1: propagate the minimum zoom forward by N keyframes
    // so the camera anticipates terrain rises
    const lookAheadWindow = 5;
    const anticipatedZooms: number[] = [];
    for (let i = 0; i < rawZooms.length; i++) {
      let minZ = rawZooms[i];
      for (let j = 1; j <= lookAheadWindow; j++) {
        const idx = Math.min(i + j, rawZooms.length - 1);
        minZ = Math.min(minZ, rawZooms[idx]);
      }
      anticipatedZooms.push(minZ);
    }

    // Pass 2: moving average for gradual transitions
    const windowSize = 7;
    const halfWindow = Math.floor(windowSize / 2);
    const smoothed: number[] = [];

    for (let i = 0; i < anticipatedZooms.length; i++) {
      let sum = 0;
      let count = 0;
      for (let j = -halfWindow; j <= halfWindow; j++) {
        const idx = Math.min(Math.max(i + j, 0), anticipatedZooms.length - 1);
        sum += anticipatedZooms[idx];
        count++;
      }
      smoothed.push(sum / count);
    }

    // Apply: always take the more conservative (lower = more zoomed out) zoom
    for (let i = 0; i < this.keyframes.length; i++) {
      this.keyframes[i].zoom = Math.min(rawZooms[i], smoothed[i]);
    }
  }

  private smoothBearings(): void {
    const windowSize = 5;
    const halfWindow = Math.floor(windowSize / 2);
    const smoothed: number[] = [];

    for (let i = 0; i < this.keyframes.length; i++) {
      const sinValues: number[] = [];
      const cosValues: number[] = [];

      for (let j = -halfWindow; j <= halfWindow; j++) {
        const idx = Math.min(Math.max(i + j, 0), this.keyframes.length - 1);
        const rad = (this.keyframes[idx].bearing * Math.PI) / 180;
        sinValues.push(Math.sin(rad));
        cosValues.push(Math.cos(rad));
      }

      const avgSin = sinValues.reduce((a, b) => a + b, 0) / sinValues.length;
      const avgCos = cosValues.reduce((a, b) => a + b, 0) / cosValues.length;
      let avgBearing = (Math.atan2(avgSin, avgCos) * 180) / Math.PI;
      smoothed.push((avgBearing + 360) % 360);
    }

    for (let i = 0; i < this.keyframes.length; i++) {
      this.keyframes[i].bearing = smoothed[i];
    }
  }

  private setupIntroOutro(): void {
    this.trackMinLat = Infinity;
    this.trackMaxLat = -Infinity;
    this.trackMinLon = Infinity;
    this.trackMaxLon = -Infinity;

    for (const p of this.points) {
      if (p.lat < this.trackMinLat) this.trackMinLat = p.lat;
      if (p.lat > this.trackMaxLat) this.trackMaxLat = p.lat;
      if (p.lon < this.trackMinLon) this.trackMinLon = p.lon;
      if (p.lon > this.trackMaxLon) this.trackMaxLon = p.lon;
    }

    const centerLon = (this.trackMinLon + this.trackMaxLon) / 2;
    const centerLat = (this.trackMinLat + this.trackMaxLat) / 2;

    const dLon = this.trackMaxLon - this.trackMinLon;
    const latRadMin = (this.trackMinLat * Math.PI) / 180;
    const latRadMax = (this.trackMaxLat * Math.PI) / 180;
    const yMin = Math.log(Math.tan(Math.PI / 4 + latRadMin / 2));
    const yMax = Math.log(Math.tan(Math.PI / 4 + latRadMax / 2));
    const dY = Math.abs(yMax - yMin);

    // 10% borders means the track fits inside 80% of the viewport width/height
    const targetW = this.width * 0.8;
    const targetH = this.height * 0.8;

    // MapLibre uses a tile width of 512 pixels at zoom 0
    const widthInPixelsAtZoom0 = 512 * (dLon / 360);
    const heightInPixelsAtZoom0 = 512 * (dY / (2 * Math.PI));

    const zoomX = Math.log2(targetW / (widthInPixelsAtZoom0 || 1));
    const zoomY = Math.log2(targetH / (heightInPixelsAtZoom0 || 1));
    const calculatedOutroZoom = Math.max(1, Math.min(20, Math.min(zoomX, zoomY)));
    this.fittedZoom = calculatedOutroZoom;

    // Intro frame starts overhead North-up, centered on the WHOLE TRACK so the full route fits on screen
    this.introFrame = {
      center: [centerLon, centerLat],
      zoom: calculatedOutroZoom,
      pitch: 0,
      bearing: 0,
    };

    // Outro frame finishes overhead North-up, centered on the WHOLE TRACK so the full route fits on screen
    this.outroFrame = {
      center: [centerLon, centerLat],
      zoom: calculatedOutroZoom,
      pitch: 0,
      bearing: 0,
    };
  }

  public getKeyframeAtProgress(progress: number): CameraKeyframe {
    const activeStart = this.introRatio;
    const activeEnd = 1 - this.outroRatio;

    // Phase 1: Intro (0% to introRatio) - Zoom-in to start of track to prevent terrain clipping
    if (progress < activeStart) {
      const t = activeStart > 0 ? progress / activeStart : 1;
      const followStart = this.keyframes[0];
      return this.blendKeyframes(this.introFrame, followStart, t);
    }

    // Phase 3: Outro (activeEnd to 100%) - Zoom-out to fit the whole track
    if (progress > activeEnd) {
      const t = this.outroRatio > 0 ? (progress - activeEnd) / this.outroRatio : 1;
      const followEnd = this.keyframes[this.keyframes.length - 1];
      return this.blendKeyframes(followEnd, this.outroFrame, t);
    }

    // Phase 2: Active (introRatio to activeEnd) - Follow the track using spline interpolation
    const activeRange = activeEnd - activeStart;
    const t = activeRange > 0 ? (progress - activeStart) / activeRange : 0;
    return this.getSplineKeyframe(t);
  }

  private getSplineKeyframe(progress: number): CameraKeyframe {
    const maxIdx = this.keyframes.length - 1;
    const idxVal = progress * maxIdx;
    const i = Math.floor(idxVal);
    const t = idxVal - i;

    const k0 = this.keyframes[Math.max(0, i - 1)];
    const k1 = this.keyframes[i];
    const k2 = this.keyframes[Math.min(maxIdx, i + 1)];
    const k3 = this.keyframes[Math.min(maxIdx, i + 2)];

    const lon = this.catmullRom(k0.center[0], k1.center[0], k2.center[0], k3.center[0], t);
    const lat = this.catmullRom(k0.center[1], k1.center[1], k2.center[1], k3.center[1], t);
    const center: [number, number] = [lon, lat];

    const zoom = this.catmullRom(k0.zoom, k1.zoom, k2.zoom, k3.zoom, t);
    const pitch = this.catmullRom(k0.pitch, k1.pitch, k2.pitch, k3.pitch, t);

    const b0 = k0.bearing;
    let b1 = k1.bearing;
    let b2 = k2.bearing;
    let b3 = k3.bearing;

    b1 = this.unwrapAngle(b0, b1);
    b2 = this.unwrapAngle(b1, b2);
    b3 = this.unwrapAngle(b2, b3);

    const bearing = (this.catmullRom(b0, b1, b2, b3, t) + 360) % 360;

    return {
      center,
      zoom,
      pitch,
      bearing,
    };
  }

  private catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
    const t2 = t * t;
    const t3 = t2 * t;
    return 0.5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 + (-p0 + 3 * p1 - 3 * p2 + p3) * t3);
  }

  private unwrapAngle(ref: number, angle: number): number {
    let diff = angle - ref;
    while (diff < -180) {
      angle += 360;
      diff = angle - ref;
    }
    while (diff > 180) {
      angle -= 360;
      diff = angle - ref;
    }
    return angle;
  }

  private blendKeyframes(k1: CameraKeyframe, k2: CameraKeyframe, t: number): CameraKeyframe {
    const isIntro = k1.pitch < k2.pitch;

    let tSmooth: number;
    let tZoom: number;
    let tPitch: number;
    let tBearing: number;

    if (isIntro) {
      // Intro: Pan smoothly, keep zoom wide, keep pitch flat, and keep bearing North-up longer
      // so the start marker doesn't get rotated off-screen early in the transition.
      tSmooth = t * t * (3 - 2 * t);
      tZoom = t * t;
      tPitch = t * t * t;
      tBearing = t * t * t;
    } else {
      // Outro: Pan smoothly, zoom out quickly, flatten pitch quickly, and return bearing to North-up quickly
      // so the full track becomes visible early.
      const tOut = 1 - (1 - t) * (1 - t);
      const tOutCubic = 1 - (1 - t) * (1 - t) * (1 - t);
      tSmooth = t * t * (3 - 2 * t);
      tZoom = tOut;
      tPitch = tOutCubic;
      tBearing = tOutCubic;
    }

    const lon = k1.center[0] + tSmooth * (k2.center[0] - k1.center[0]);
    const lat = k1.center[1] + tSmooth * (k2.center[1] - k1.center[1]);
    const zoom = k1.zoom + tZoom * (k2.zoom - k1.zoom);
    const pitch = k1.pitch + tPitch * (k2.pitch - k1.pitch);

    const b2 = this.unwrapAngle(k1.bearing, k2.bearing);
    const bearing = (k1.bearing + tBearing * (b2 - k1.bearing) + 360) % 360;

    return {
      center: [lon, lat],
      zoom,
      pitch,
      bearing,
    };
  }

  /**
   * Returns zoom and pan constraints for manual map interactions.
   * Prevents users from exploring far beyond the course area
   * and burning map provider tile credits.
   *
   * The padded bounds are adjusted to match the viewport aspect ratio
   * so that panning feels equal in both screen directions.
   */
  public getMapConstraints(): MapConstraints {
    const MIN_PADDING_DEG = 0.005; // ~500m floor for degenerate tracks
    const PADDING_RATIO = 0.25;

    const dLon = this.trackMaxLon - this.trackMinLon;
    const dLat = this.trackMaxLat - this.trackMinLat;

    let padLon = Math.max(MIN_PADDING_DEG, dLon * PADDING_RATIO);
    let padLat = Math.max(MIN_PADDING_DEG, dLat * PADDING_RATIO);

    // Adjust padding so padded bounds match viewport aspect ratio.
    // Without this, a 16:9 viewport with a square-ish track gets
    // almost no horizontal pan room but lots of vertical room.
    const viewportAspect = this.width / this.height;
    const boundsLon = dLon + 2 * padLon;
    const boundsLat = dLat + 2 * padLat;

    // Correct for longitude compression at the track's latitude
    const centerLat = (this.trackMinLat + this.trackMaxLat) / 2;
    const lonScale = Math.cos((centerLat * Math.PI) / 180);
    const boundsVisualWidth = boundsLon * lonScale;
    const currentAspect = boundsVisualWidth / boundsLat;

    if (currentAspect < viewportAspect) {
      // Bounds too narrow horizontally — expand lon padding
      const targetLon = (boundsLat * viewportAspect) / lonScale;
      padLon = Math.max(padLon, (targetLon - dLon) / 2);
    } else {
      // Bounds too short vertically — expand lat padding
      const targetLat = boundsVisualWidth / viewportAspect;
      padLat = Math.max(padLat, (targetLat - dLat) / 2);
    }

    return {
      minZoom: Math.max(1, this.fittedZoom - 2),
      maxBounds: [
        [this.trackMinLon - padLon, this.trackMinLat - padLat],
        [this.trackMaxLon + padLon, this.trackMaxLat + padLat],
      ],
    };
  }

  public getTelemetryAtProgress(progress: number): TelemetryPoint {
    const activeStart = this.introRatio;
    const activeEnd = 1 - this.outroRatio;
    if (progress < activeStart) {
      return this.getInterpolatedPoint(0);
    }
    if (progress > activeEnd) {
      return this.getInterpolatedPoint(this.totalDistance);
    }
    const activeRange = activeEnd - activeStart;
    const t = activeRange > 0 ? (progress - activeStart) / activeRange : 0;
    const targetDist = t * this.totalDistance;
    return this.getInterpolatedPoint(targetDist);
  }
}
